import type { BillingCurrency } from '@contracts/payment/Currency';
import type { PaymentMethod } from '@contracts/payment/PaymentMethod';
import Skeleton from '@mui/material/Skeleton/Skeleton';
import { useAvailableCurrencies } from '@src/clients/financialSettings/hooks/useAvailableCurrencies';
import { MenuItem, Select } from '@src/common/components/Select';
import { css } from '@src/common/styles/Styles';
import { useTranslation } from 'react-i18next';

interface Props {
  disabled: boolean;
  paymentMethod?: PaymentMethod;
  onCurrencyChange: ({
    billingCurrency,
  }: {
    billingCurrency: BillingCurrency;
  }) => void;
  defaultCurrency: BillingCurrency | undefined;
}

export const InvoiceCurrencyDropdown = ({
  disabled,
  paymentMethod,
  onCurrencyChange,
  defaultCurrency,
}: Props) => {
  const { t } = useTranslation();
  const { currencies, isLoading } = useAvailableCurrencies();
  const hasCAD = !!currencies.find((currency) => currency === 'CAD');
  const hasUSD = !!currencies.find((currency) => currency === 'USD');
  const hasALL = hasCAD && hasUSD;

  const optionCAD = { label: t('currency.cad'), value: 'CAD' };
  const optionUSD = { label: t('currency.usd'), value: 'USD' };
  const optionALL = { label: t('currency.all'), value: 'ALL' };

  const getDropdownOptions = () => {
    const options = [];

    if (hasCAD) options.push(optionCAD);
    if (hasUSD) options.push(optionUSD);
    if (hasALL && paymentMethod === 'LINE_OF_CREDIT') options.push(optionALL);

    return options;
  };

  if (isLoading) {
    return (
      <Skeleton
        data-testid="loading-currencies-skeleton"
        className={skeletonSize}
      />
    );
  }

  return (
    <Select
      className={width}
      disabled={disabled || isLoading}
      value={defaultCurrency}
      label={t('reviewBilling.invoiceCurrency')}
      onChange={(e) =>
        onCurrencyChange({ billingCurrency: e.target.value as BillingCurrency })
      }
    >
      {getDropdownOptions().map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const DROPDOWN_WIDTH = '315px';

const width = css({
  width: DROPDOWN_WIDTH,
});

const skeletonSize = css({
  width: DROPDOWN_WIDTH,
  height: '55px',
});
